import { enc, httpDelete, httpGet, httpPatch, httpPost } from './http'
import { SEND_IDEMPOTENCY_KEY } from '../config'
import type {
  AllUsersCredit,
  Billing,
  BillingAccount,
  CheckoutSession,
  CreditInfo,
  CreditTransaction,
  Invoice,
  InvoiceReportSettings,
  PaymentMethod,
  Plan,
  Product,
  OnetimeProduct,
  Project,
  ProjectUser,
  Subscription,
  TelegramChatInfo,
  TelegramChatMember,
  TelegramChatMemberView,
  TelegramChatUserManager,
  TelegramConnection,
  Transaction,
  TransactionStatistics,
  ServiceAccount,
  ConnectionSettings,
  ProjectConnectionSettings,
  UsageFee,
  UsageFeeBlock,
  UsageFeeItem,
} from './types'

/**
 * Every api.feesubscription.com call this back-office makes, one function
 * per legacy Angular service method (names follow the legacy services:
 * project.service.ts, plan.service.ts, product.service.ts,
 * usagefee.service.ts, payment.service.ts, admin.service.ts,
 * service-account.service.ts, connection.service.ts, telegram.service.ts).
 * Paths are the legacy ones, verbatim. The 18 legacy UI calls the backend
 * never routed (port plan §1.3: customusagefees, /payment/subscription/{p},
 * /payment/product/{p}[/notneeded], /oneTimeToken/{scope},
 * POST …/payment/sessions/{id}, GET …/metas, statistics/*) are NOT here.
 */

const p = (projectId: string) => `/projects/${enc(projectId)}`

// --- projects / users (B2) --------------------------------------------------

export const projects = {
  search: (q?: string) => httpGet<Project[]>('/projects', { query: q ? { q } : undefined }),
  view: (projectId: string) => httpGet<Project>(p(projectId)),
  /** `201 {"id": "<new id>"}` */
  add: (name: string) => httpPost<{ id: string }>('/projects', { name }, { softErrors: true, invalidates: ['/projects'] }),
  /** Body `{metas: {...}}`. A secret meta key sent EMPTY leaves the stored
   * secret unchanged (backend contract) — so the form never needs it back. */
  setMetas: (projectId: string, metas: Record<string, unknown>) =>
    httpPatch<Project>(`${p(projectId)}/metas`, { metas }, { softErrors: true, invalidates: [p(projectId), '/projects'] }),
  users: (projectId: string) => httpGet<ProjectUser[]>(`${p(projectId)}/users`),
  addUser: (projectId: string, username: string) =>
    httpPost<ProjectUser>(`${p(projectId)}/users`, { username }, { softErrors: true }),
  removeUser: (projectId: string, username: string) => httpDelete<unknown>(`${p(projectId)}/users/${enc(username)}`),
  addRole: (projectId: string, username: string, role: string) =>
    httpPost<unknown>(`${p(projectId)}/users/${enc(username)}/roles/${enc(role)}`, undefined, { softErrors: true }),
  removeRole: (projectId: string, username: string, role: string) =>
    httpDelete<unknown>(`${p(projectId)}/users/${enc(username)}/roles/${enc(role)}`),
}

// --- catalogue (B3 reads, B3b writes — admin only) --------------------------

export const plans = {
  search: (projectId: string) => httpGet<Plan[]>(`${p(projectId)}/plans`),
  view: (projectId: string, id: string) => httpGet<Plan>(`${p(projectId)}/plans/${enc(id)}`),
  add: (projectId: string, body: Record<string, unknown>) =>
    httpPost<Plan>(`${p(projectId)}/plans`, body, { softErrors: true }),
  modify: (projectId: string, id: string, body: Record<string, unknown>) =>
    httpPatch<Plan>(`${p(projectId)}/plans/${enc(id)}`, body, { softErrors: true }),
  subscriptionsOfPlan: (projectId: string, id: string) =>
    httpGet<Subscription[]>(`${p(projectId)}/admin/plans/${enc(id)}/subscriptions`),
  plansOfUser: (projectId: string, email: string) =>
    httpGet<Plan[]>(`${p(projectId)}/admin/plans`, { query: { email } }),
}

export const products = {
  search: (projectId: string) => httpGet<Product[]>(`${p(projectId)}/products`),
  view: (projectId: string, id: string) => httpGet<Product>(`${p(projectId)}/products/${enc(id)}`),
  add: (projectId: string, body: Record<string, unknown>) =>
    httpPost<Product>(`${p(projectId)}/products`, body, { softErrors: true }),
  modify: (projectId: string, id: string, body: Record<string, unknown>) =>
    httpPatch<Product>(`${p(projectId)}/products/${enc(id)}`, body, { softErrors: true }),
  usersOfProduct: (projectId: string, id: string) =>
    httpGet<OnetimeProduct[]>(`${p(projectId)}/admin/products/${enc(id)}/users`),
  productsOfUser: (projectId: string, email: string) =>
    httpGet<Product[]>(`${p(projectId)}/admin/products`, { query: { email } }),
}

export const usagefees = {
  search: (projectId: string) => httpGet<UsageFee[]>(`${p(projectId)}/usagefees`),
  view: (projectId: string, id: string) => httpGet<UsageFee>(`${p(projectId)}/usagefees/${enc(id)}`),
  add: (projectId: string, body: Record<string, unknown>) =>
    httpPost<UsageFee>(`${p(projectId)}/usagefees`, body, { softErrors: true }),
  modify: (projectId: string, id: string, body: Record<string, unknown>) =>
    httpPatch<UsageFee>(`${p(projectId)}/usagefees/${enc(id)}`, body, { softErrors: true }),
  blocks: (projectId: string) => httpGet<UsageFeeBlock[]>(`${p(projectId)}/usagefees/blocks`),
  itemsByMonth: (projectId: string, month: string) =>
    httpGet<UsageFeeItem[]>(`${p(projectId)}/usagefees/items/byMonth/${enc(month)}`),
}

// --- credits (B3 reads, B4 admin writes) ------------------------------------

export interface CreditChangeBody {
  email: string
  currency: string
  /** Sent as the exact string the admin typed — never parsed to a float. */
  amount: string
  cause: string
  reference: string
}

export const credits = {
  search: (projectId: string) => httpGet<CreditInfo[]>(`${p(projectId)}/credits`),
  view: (projectId: string, currency: string) => httpGet<CreditInfo>(`${p(projectId)}/credits/${enc(currency)}`),
  userCredits: (projectId: string, email: string) =>
    httpGet<CreditInfo[]>(`${p(projectId)}/admin/credit/all`, { query: { email } }),
  allUsers: (projectId: string) => httpGet<AllUsersCredit>(`${p(projectId)}/admin/credit/allusers`),
  add: (projectId: string, body: CreditChangeBody) =>
    httpPost<CreditTransaction>(`${p(projectId)}/admin/credit/add`, body, { softErrors: true, emptyArrayIsRefusal: true }),
  remove: (projectId: string, body: CreditChangeBody) =>
    httpPost<CreditTransaction>(`${p(projectId)}/admin/credit/remove`, body, { softErrors: true, emptyArrayIsRefusal: true }),
}

// --- payment reads (B3) -----------------------------------------------------

export const paymentReads = {
  method: (projectId: string) => httpGet<PaymentMethod>(`${p(projectId)}/payment/method`),
  methodOfUser: (projectId: string, email: string) =>
    httpGet<PaymentMethod>(`${p(projectId)}/admin/payment/method`, { query: { email } }),
  billing: (projectId: string) => httpGet<Billing>(`${p(projectId)}/payment/billing`),
  billingOfUser: (projectId: string, email: string) =>
    httpGet<Billing>(`${p(projectId)}/admin/payment/billing`, { query: { email } }),
  billingAccount: (projectId: string) => httpGet<BillingAccount>(`${p(projectId)}/payment/billingaccount`),
  transactions: (projectId: string) => httpGet<Transaction[]>(`${p(projectId)}/payment/transactions`),
  transactionsOfUser: (projectId: string, email: string) =>
    httpGet<Transaction[]>(`${p(projectId)}/admin/payment/transactions`, { query: { email } }),
  /** `GET …/payment/transactions/statistics` — the project dashboard's
   * last-12-months totals + monthly history (project-view). */
  transactionStatistics: (projectId: string) =>
    httpGet<TransactionStatistics>(`${p(projectId)}/payment/transactions/statistics`),
  invoices: (projectId: string) => httpGet<Invoice[] | { items?: Invoice[] }>(`${p(projectId)}/payment/invoices`),
  session: (projectId: string, sessionId: string) =>
    httpGet<CheckoutSession | null>(`${p(projectId)}/payment/sessions/${enc(sessionId)}`),
  allInvoices: (projectId: string, month: string) =>
    httpGet<Invoice[]>(`${p(projectId)}/admin/payment/invoices/all`, { query: { month } }),
  invoiceReport: (projectId: string) =>
    httpGet<InvoiceReportSettings>(`${p(projectId)}/admin/payment/invoices/report`),
  modifyInvoiceReport: (projectId: string, body: InvoiceReportSettings) =>
    httpPatch<InvoiceReportSettings>(`${p(projectId)}/admin/payment/invoices/report`, body, { softErrors: true }),
  sendInvoiceReport: (projectId: string, month: string) =>
    httpPost<unknown>(`${p(projectId)}/admin/payment/invoices/report/send`, undefined, { query: { month } }),
}

// --- money movement (B5) — BUSINESS-CRITICAL --------------------------------

/** Legacy `PaymentService.createPlanSession`/`createProductSession`/
 * `createCreditSession` body (payment.service.ts). Only the keys the legacy
 * UI sent; the backend relays them into the Checkout metadata. */
export interface SessionRequest {
  type: 'plan' | 'product' | 'credit'
  cause?: string
  plans?: string
  products?: string
  /** String, never a float (RULES rule 3). */
  credits?: string
  creditCurrency?: string
  setupmethod?: string
  successUrl?: string
  cancelUrl?: string
  oldSessionId?: string
}

export interface StartOnlineRequest {
  type: 'plan' | 'product' | 'credit'
  cause: string
  plans?: string
  products?: string
  credits?: string
  creditCurrency?: string
}

function idemHeaders(key: string | undefined): Record<string, string> | undefined {
  return SEND_IDEMPOTENCY_KEY && key ? { 'Idempotency-Key': key } : undefined
}

export const money = {
  createSession: (projectId: string, body: SessionRequest) =>
    httpPost<CheckoutSession>(`${p(projectId)}/payment/sessions`, body, { softErrors: true }),
  checkSession: (projectId: string, sessionId: string) =>
    httpPatch<CheckoutSession | null>(`${p(projectId)}/payment/sessions/${enc(sessionId)}/check`, undefined, { softErrors: true }),
  expireSession: (projectId: string, sessionId: string) =>
    httpPatch<CheckoutSession | null>(`${p(projectId)}/payment/sessions/${enc(sessionId)}/expire`, undefined, { softErrors: true }),
  /** Off-session charge of the saved card. The idempotency key is always
   * generated per submit; it is SENT only when SEND_IDEMPOTENCY_KEY is on
   * (see src/config.ts for why it is off today). */
  startOnline: (projectId: string, body: StartOnlineRequest, idempotencyKey?: string) =>
    httpPost<Transaction | null>(`${p(projectId)}/payment/startonline`, body, {
      softErrors: true,
      headers: idemHeaders(idempotencyKey),
    }),
  cancelSubscription: (projectId: string, planId: string) =>
    httpPatch<Subscription>(`${p(projectId)}/payment/subscription/${enc(planId)}/cancel`, undefined, { softErrors: true }),
  restartSubscription: (projectId: string, planId: string) =>
    httpPatch<Subscription | Transaction>(`${p(projectId)}/payment/subscription/${enc(planId)}/restart`, undefined, {
      softErrors: true,
    }),
  cancelPayment: (projectId: string) =>
    httpPatch<PaymentMethod>(`${p(projectId)}/payment/cancel`, undefined, { softErrors: true }),
  modifyBilling: (projectId: string, billing: Billing) =>
    httpPatch<Billing>(`${p(projectId)}/payment/billing`, billing, { softErrors: true }),
  // admin, acting on an end-user identified by e-mail
  startOnlineForUser: (projectId: string, email: string, body: StartOnlineRequest, idempotencyKey?: string) =>
    httpPost<Transaction | null>(`${p(projectId)}/admin/payment/startonline`, body, {
      query: { email },
      softErrors: true,
      emptyArrayIsRefusal: true,
      headers: idemHeaders(idempotencyKey),
    }),
  startSubscriptionForUser: (projectId: string, email: string, plan: string) =>
    httpPost<Subscription>(`${p(projectId)}/admin/payment/subscription`, undefined, {
      query: { email, plan },
      softErrors: true,
      emptyArrayIsRefusal: true,
    }),
  modifySubscriptionOfUser: (
    projectId: string,
    email: string,
    plan: string,
    body: { status: number; expiresAtDate: string },
  ) =>
    httpPatch<Subscription>(`${p(projectId)}/admin/payment/subscription`, body, {
      query: { email, plan },
      softErrors: true,
      emptyArrayIsRefusal: true,
    }),
  markProductPaidForUser: (projectId: string, email: string, product: string) =>
    httpPost<OnetimeProduct>(`${p(projectId)}/admin/payment/product`, undefined, {
      query: { email, product },
      softErrors: true,
      emptyArrayIsRefusal: true,
    }),
}

// --- Telegram chat user manager / connections (B7) --------------------------

const tcum = (projectId: string) => `${p(projectId)}/payment/telegramchatusermanager`

export const telegram = {
  managers: (projectId: string) => httpGet<TelegramChatUserManager[]>(tcum(projectId)),
  manager: (projectId: string, id: string) => httpGet<TelegramChatUserManager>(`${tcum(projectId)}/${enc(id)}`),
  addManager: (projectId: string, body: Partial<TelegramChatUserManager>) =>
    httpPost<TelegramChatUserManager>(tcum(projectId), body, { softErrors: true }),
  modifyManager: (projectId: string, id: string, body: Partial<TelegramChatUserManager>) =>
    httpPatch<TelegramChatUserManager>(`${tcum(projectId)}/${enc(id)}`, body, { softErrors: true }),
  chatInfo: (projectId: string, id: string) => httpGet<TelegramChatInfo>(`${tcum(projectId)}/${enc(id)}/chat/info`),
  syncChatInfo: (projectId: string, id: string) =>
    httpPatch<TelegramChatInfo>(`${tcum(projectId)}/${enc(id)}/chat/info`, undefined, { softErrors: true }),
  members: (projectId: string, id: string) =>
    httpGet<TelegramChatMember[]>(`${tcum(projectId)}/${enc(id)}/chat/members`),
  member: (projectId: string, id: string, userId: string) =>
    httpGet<TelegramChatMemberView>(`${tcum(projectId)}/${enc(id)}/chat/members/${enc(userId)}`),
  syncMember: (projectId: string, id: string, userId: string) =>
    httpPost<unknown>(`${tcum(projectId)}/${enc(id)}/chat/members/${enc(userId)}/synctotelegram`, undefined, {
      softErrors: true,
    }),
  syncAllMembers: (projectId: string, id: string) =>
    httpPost<unknown>(`${tcum(projectId)}/${enc(id)}/chat/members/synctotelegram`, undefined, { softErrors: true }),
  connection: () => httpGet<TelegramConnection>('/connections/telegram'),
  connect: (telegramData: Record<string, unknown>) =>
    httpPatch<TelegramConnection>('/connections/telegram/connect', { telegramData }, { softErrors: true, invalidates: ['/connections'] }),
}

// --- service accounts (B2, project admin only) ------------------------------

const sa = (projectId: string) => `${p(projectId)}/serviceAccounts`

export const serviceAccounts = {
  search: (projectId: string) => httpGet<ServiceAccount[]>(sa(projectId)),
  add: (projectId: string) => httpPost<ServiceAccount>(sa(projectId), {}, { softErrors: true }),
  modify: (projectId: string, id: string, body: Pick<ServiceAccount, 'description' | 'comment'>) =>
    httpPatch<ServiceAccount>(`${sa(projectId)}/${enc(id)}`, body, { softErrors: true }),
  revoke: (projectId: string, id: string) => httpDelete<ServiceAccount>(`${sa(projectId)}/${enc(id)}`),
}

// --- connection settings (B2) -------------------------------------------------

export const connections = {
  settings: (projectId: string) => httpGet<ConnectionSettings>(`${p(projectId)}/connectionsettings`),
  modifySettings: (projectId: string, body: ConnectionSettings) =>
    httpPatch<ConnectionSettings>(`${p(projectId)}/connectionsettings`, body, { softErrors: true }),
  allSettings: () => httpGet<ProjectConnectionSettings[]>('/connectionsettings'),
  modifyAllSettings: (items: ProjectConnectionSettings[]) =>
    httpPatch<ProjectConnectionSettings[]>('/connectionsettings', { items }, { softErrors: true, invalidates: ['/connectionsettings'] }),
}
